import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import telephoneVector from "../../assets/telephone.svg";
import mailVector from "../../assets/mailVector.svg";
import homeIcon from "../../assets/homeIcon.svg";

const SendMessage = ({ contact }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    location: "",
    trainingTopics: "",
    additionalNotes: "",
  });

  const [focusedField, setFocusedField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/send-message`,
        formData,
      );
      console.log("Form submitted successfully:", response.data);
      // Reset form after successful submission
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        location: "",
        trainingTopics: "",
        additionalNotes: "",
      });
      alert("Message sent successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Error sending message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="relative w-full bg-gradient-to-br from-gray-50 to-blue-50 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-br from-red-100 to-orange-100 rounded-full blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="relative z-10 lg:flex lg:items-center lg:gap-12 py-8 lg:py-16 px-4 lg:px-8 max-w-7xl mx-auto"
      >
        {/* Contact Form */}
        <motion.div
          variants={itemVariants}
          className="w-full lg:w-1/2 xl:w-[55%]"
        >
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-6 md:p-8 lg:p-10">
            {/* Header */}
            <motion.div
              variants={itemVariants}
              className="mb-8 text-center lg:text-left"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">
                Have A Question Or Looking To{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#183864] to-[#B02526]">
                  Book A Private Training?
                </span>
              </h2>
              <p className="text-gray-600 text-lg">
                Please fill in our inquiry for more information
              </p>
            </motion.div>

            {/* Form */}
            <motion.form
              variants={itemVariants}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("firstName")}
                    onBlur={() => setFocusedField("")}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent"
                    placeholder="First Name"
                    required
                  />
                  <label
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      focusedField === "firstName" || formData.firstName
                        ? "-top-2 text-sm text-blue-600 bg-white px-2"
                        : "top-4 text-gray-500"
                    }`}
                  >
                    First Name *
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("lastName")}
                    onBlur={() => setFocusedField("")}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent"
                    placeholder="Last Name"
                    required
                  />
                  <label
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      focusedField === "lastName" || formData.lastName
                        ? "-top-2 text-sm text-blue-600 bg-white px-2"
                        : "top-4 text-gray-500"
                    }`}
                  >
                    Last Name *
                  </label>
                </div>
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField("")}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent"
                    placeholder="Email"
                    required
                  />
                  <label
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      focusedField === "email" || formData.email
                        ? "-top-2 text-sm text-blue-600 bg-white px-2"
                        : "top-4 text-gray-500"
                    }`}
                  >
                    Email Address *
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("phone")}
                    onBlur={() => setFocusedField("")}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent"
                    placeholder="Phone"
                    required
                  />
                  <label
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      focusedField === "phone" || formData.phone
                        ? "-top-2 text-sm text-blue-600 bg-white px-2"
                        : "top-4 text-gray-500"
                    }`}
                  >
                    Phone Number *
                  </label>
                </div>
              </div>

              {/* Location and Training Topics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("location")}
                    onBlur={() => setFocusedField("")}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent"
                    placeholder="Location"
                  />
                  <label
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      focusedField === "location" || formData.location
                        ? "-top-2 text-sm text-blue-600 bg-white px-2"
                        : "top-4 text-gray-500"
                    }`}
                  >
                    Location
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    name="trainingTopics"
                    value={formData.trainingTopics}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("trainingTopics")}
                    onBlur={() => setFocusedField("")}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent"
                    placeholder="Training Topics"
                  />
                  <label
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      focusedField === "trainingTopics" ||
                      formData.trainingTopics
                        ? "-top-2 text-sm text-blue-600 bg-white px-2"
                        : "top-4 text-gray-500"
                    }`}
                  >
                    Training Topics
                  </label>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="relative">
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField("additionalNotes")}
                  onBlur={() => setFocusedField("")}
                  rows="4"
                  className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white transition-all duration-300 outline-none text-gray-800 peer placeholder-transparent resize-none"
                  placeholder="Additional Notes"
                />
                <label
                  className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                    focusedField === "additionalNotes" ||
                    formData.additionalNotes
                      ? "-top-2 text-sm text-blue-600 bg-white px-2"
                      : "top-4 text-gray-500"
                  }`}
                >
                  Additional Notes
                </label>
              </div>

              {/* Submit Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex justify-center sm:justify-end mt-8"
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 py-4 bg-red-700 hover:bg-red-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </motion.div>
            </motion.form>

            {/* Private Group Inquiry Link */}
            <div className="pt-6 border-t border-gray-200 mt-8">
              <Link
                to="/contact-private"
                className="inline-flex items-center gap-2 font-bold text-lg md:text-xl text-[#B02526] hover:text-[#8B1E1F] transition-colors duration-300"
              >
                <span>Private Group Inquiry</span>
                <motion.svg
                  whileHover={{ x: 4 }}
                  className="w-5 h-5"
                  viewBox="0 0 7 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.73242 1.9043L6.00004 7.22249L1.73242 12.5407"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          variants={itemVariants}
          className="w-full lg:w-1/2 xl:w-[45%] mt-8 lg:mt-0"
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-lg border border-white/20">
            <h2 className="font-bold text-xl md:text-2xl text-[#183864] mb-8">
              Contact Info
            </h2>

            {/* Phone Numbers */}
            <div className="flex items-start gap-4 mb-8">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#B02526]/10 rounded-full flex items-center justify-center">
                  <img src={telephoneVector} alt="Phone" className="w-6 h-6" />
                </div>
              </div>
              <div className="flex-1">
                <div className="space-y-2">
                  {contact?.contactInfo?.length > 0 ? (
                    contact.contactInfo.map((phone, index) => (
                      <p
                        key={index}
                        className="text-[#183864] text-sm md:text-base"
                      >
                        {phone}
                      </p>
                    ))
                  ) : (
                    <p className="text-[#183864] text-sm md:text-base">
                      +1 (555) 123-4567
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4 mb-8">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#B02526]/10 rounded-full flex items-center justify-center">
                  <img src={mailVector} alt="Email" className="w-6 h-6" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-[#183864] text-sm md:text-base">
                  {contact?.email || "info@prosafe.com"}
                </p>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#B02526]/10 rounded-full flex items-center justify-center">
                  <img src={homeIcon} alt="Address" className="w-6 h-6" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-[#183864] text-sm md:text-base">
                  {contact?.address ||
                    "123 Training Street, City, Province, Postal Code"}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SendMessage;
